import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Dokumentation, PublicProjektSag } from 'src/app/api/models';
import { Rettighed } from 'src/app/core/models/rettighed';
import { Direction } from '../../enums/direction-enum';
import { CurrentProjektService } from '../current-projekt/current-projekt.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentSagService {
  sag$: Observable<PublicProjektSag>;
  disableInput$: Observable<boolean>;
  isGenbrugProcessing$ = new BehaviorSubject<boolean>(false);
  currentSagId$ = new BehaviorSubject<string>(undefined);

  constructor(private currentProjektService: CurrentProjektService) {}

  initialize() {
    // console.log('initialize first: ', this.sag$);

    this.sag$ = combineLatest([this.currentProjektService.projekt$, this.currentSagId$]).pipe(
      filter(x => !!x[0] && !!x[1]),
      map(x => {
        const projekt = x[0];
        const sagId = x[1];

        return projekt.sager.find(sag => sag.id === sagId);
      })
    );

    this.disableInput$ = combineLatest([this.sag$, this.currentProjektService.isReadonly$]).pipe(
      map(([sag, isProjektReadonly]) => {
        return isProjektReadonly || !this.hasPermission(sag, Rettighed.Dokumentation) ? true : null;
      })
    );
    // console.log('initialize last: ', this.sag$);
  }

  hasPermission(
    sag: PublicProjektSag,
    requiredRettighed: Rettighed,
    direction: Direction = Direction.AtLeast
  ): boolean {
    if (sag.erAfsluttet) {
      return false;
    }

    return this.currentProjektService.hasPermission(requiredRettighed, direction);
  }

  /**
   * Updates faerdiggrad on current Projekt for a given Dokumentation
   * @param dokumentation Dokumentation containing the updated Faerdiggrad
   */
  ensureUpdatedFaerdiggrad(dokumentation: Dokumentation) {
    const projektDok = this.currentProjektService.projekt$.value.sager
      .find(s => s.id === this.currentSagId$.value)
      .sagFaser.flatMap(sf => sf.dokumentationer)
      .find(d => d.id === dokumentation.id);

    if (!!projektDok && projektDok.faerdigGrad !== dokumentation.faerdigGrad) {
      projektDok.faerdigGrad = dokumentation.faerdigGrad;
      this.currentProjektService.projekt$.next(this.currentProjektService.projekt$.value);
    }
  }

  isFaseDisabled(id: string): Observable<boolean> {
    return combineLatest([this.sag$, this.disableInput$]).pipe(
      map(([sag, isDisabled]) => {
        if (isDisabled) {
          return isDisabled;
        }
        if (this.sag$) {
          for (const sagFase of sag.sagFaser) {
            if (sagFase.dokumentationer.some(dok => dok.id === id)) {
              return sagFase.isDisabled ? true : null;
            }
          }
        }
        return null;
      })
    );
  }

  setDisableBegrundelsesTekstFromDokumentation(dokumentationId: string): Observable<string | null> {
    return combineLatest([this.sag$, this.currentProjektService.isReadonly$]).pipe(
      map(([sag, projektUdtagetTilSletning]) => {
        if (projektUdtagetTilSletning) {
          return 'Laasning.Begrundelsestekst.ProjektAfsluttet';
        }

        if (sag.erAfsluttet) {
          return 'Laasning.Begrundelsestekst.SagAfsluttet';
        }

        if (!this.hasPermission(sag, Rettighed.Dokumentation)) {
          return 'Laasning.Begrundelsestekst.KunLaeserettigheder';
        }

        const fase = sag.sagFaser.find(sf =>
          sf.dokumentationer.some(dok => dok.id === dokumentationId)
        );

        return fase?.isDisabled
          ? 'Laasning.Begrundelsestekst.ManglendeTilladelse'
          : null;
      })
    );
  }
}
